/**
 * 09.10.26. Не удалять по требованию владельца системы.
 * Живое ребро клиники: статус своим цветом и время последней проверки.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useTenantScope } from '@/hooks/useTenantScope';
import { linearCanvas, linearStatusClass } from '@/features/monitoring/linearCanvas';

type EdgeItem = {
  key: string;
  status: string;
  detail?: string;
  checked_at?: string;
};

type EdgesPayload = {
  checked_at?: string;
  integrations?: EdgeItem[];
};

const LABEL: Record<string, string> = {
  data_layer: 'Датаслой',
  knowledge_hub: 'Clinic Knowledge Hub',
};

const STATUS: Record<string, string> = {
  ok: 'в норме',
  warn: 'деградация',
  error: 'недоступно',
  unconfigured: 'не настроено',
  unknown: 'нет данных',
};

export function formatCheckedAt(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function edgeClass(status: string): string {
  return linearStatusClass(status);
}

export function DependencyStrip({ edge, compact = false }: { edge: 'data_layer' | 'knowledge_hub'; compact?: boolean }) {
  const { tenantId } = useTenantScope();
  const query = useQuery({
    queryKey: ['health-edges', tenantId, edge],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const response = await apiClient.get<EdgesPayload>('/health/edges');
      return response.data;
    },
  });
  const item = query.data?.integrations?.find((row) => row.key === edge);
  const checked = formatCheckedAt(item?.checked_at || query.data?.checked_at);
  if (!tenantId) return null;
  if (compact) {
    const status = query.isLoading ? 'проверяем' : STATUS[item?.status || 'unknown'];
    return (
      <p className="rounded-lg border border-shell-border bg-shell-elevated px-3 py-2 text-xs text-shell-text" data-testid={`edge-${edge}`}>
        {LABEL[edge]}: {status}{checked ? ` · проверено ${checked}` : ''}
      </p>
    );
  }
  return (
    <div className={linearCanvas.canvas} data-testid={`edge-${edge}`}>
      <div className={linearCanvas.kicker}>Связь</div>
      <div className={linearCanvas.row}>
        <span className={linearCanvas.cardTitle}>{LABEL[edge]}</span>
        <span className={linearStatusClass(item?.status || 'unknown')}>
          {query.isLoading ? 'проверяем' : STATUS[item?.status || 'unknown']}
          {item?.detail ? ` · ${item.detail}` : ''}
        </span>
      </div>
      {checked ? <div className={linearCanvas.meta}>Проверено {checked}</div> : null}
      {item?.status === 'error' ? (
        <p className={linearCanvas.error}>Связь недоступна. Действие через неё сейчас не предлагается.</p>
      ) : null}
    </div>
  );
}
